import { AuthError } from '@classgo/shared/auth';
import { Button, Input } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { ApiError } from '../../api/client';
import { useApi } from '../../api/provider';
import { useAuthStore, useCognito } from '../../auth/auth-provider';
import { useKitT } from '../../i18n';
import { AuthLayout, FormError } from '../auth-layout';

type PublicInvitation = {
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  schoolName: string;
  inviteeName: string;
  email: string;
  role: 'SCHOOL_OWNER' | 'SCHOOL_STAFF' | 'TEACHER' | 'GUARDIAN';
  expiresAt: string;
  students: { name: string; group: string | null }[];
  assignments: { group: string; subject: string | null }[];
};

/**
 * The page an invitation link opens (ticket #63): who invites, as what, what the person will
 * find inside; a password chosen once, then straight into the app. Spent and expired links
 * say so instead of showing the form.
 */
export const AcceptInvitationScreen = ({ token }: { token: string }) => {
  const t = useKitT();
  const api = useApi();
  const cognito = useCognito();
  const auth = useAuthStore();
  const router = useRouter();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const invitation = useQuery({
    queryKey: ['public', 'invitation', token],
    queryFn: () =>
      api.request<PublicInvitation>(`/public/invitations/${encodeURIComponent(token)}`, {
        auth: false,
      }),
    retry: false,
  });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmation) {
      setError(t('login.newPassword.mismatch'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const accepted = await api.request<{ email: string }>(
        `/public/invitations/${encodeURIComponent(token)}/accept`,
        { method: 'POST', body: { password }, auth: false },
      );
      const result = await cognito.signIn(accepted.email, password);
      if (result.kind === 'signedIn') {
        auth.completeSignIn(accepted.email, result.tokens);
        router.history.push('/');
      } else {
        router.history.push('/login');
      }
    } catch (thrown) {
      if (thrown instanceof ApiError) {
        setError(thrown.message);
      } else if (thrown instanceof AuthError) {
        setError(t(`auth.error.${thrown.code}`));
      } else {
        setError(t('auth.error.UNKNOWN'));
      }
    } finally {
      setBusy(false);
    }
  };

  if (invitation.isPending) {
    return (
      <AuthLayout title={t('invite.title')}>
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      </AuthLayout>
    );
  }
  if (invitation.isError || !invitation.data) {
    return (
      <AuthLayout title={t('invite.notFound.title')} subtitle={t('invite.notFound.help')}>
        <Link
          to="/login"
          search={{ redirect: undefined }}
          className="text-brand-600 underline underline-offset-2 hover:decoration-2"
        >
          {t('forgot.backToLogin')}
        </Link>
      </AuthLayout>
    );
  }
  const data = invitation.data;
  if (data.status !== 'pending') {
    return (
      <AuthLayout
        title={t(`invite.${data.status}.title`)}
        subtitle={t(`invite.${data.status}.help`)}
      >
        <Link
          to="/login"
          search={{ redirect: undefined }}
          className="text-brand-600 underline underline-offset-2 hover:decoration-2"
        >
          {t('forgot.backToLogin')}
        </Link>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout
      title={t('invite.title')}
      subtitle={t('invite.subtitle', {
        school: data.schoolName,
        name: data.inviteeName,
        role: t(`invite.role.${data.role}`),
      })}
    >
      {data.students.length > 0 ? (
        <div className="rounded-control bg-info-100 px-3 py-2 text-sm text-info-700">
          <p className="font-medium">{t('invite.students')}</p>
          <ul className="mt-1 list-disc pl-5">
            {data.students.map((student) => (
              <li key={`${student.name}-${student.group ?? ''}`}>
                {student.name}
                {student.group ? ` · ${student.group}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {data.assignments.length > 0 ? (
        <div className="rounded-control bg-info-100 px-3 py-2 text-sm text-info-700">
          <p className="font-medium">{t('invite.assignments')}</p>
          <ul className="mt-1 list-disc pl-5">
            {data.assignments.map((assignment) => (
              <li key={`${assignment.group}-${assignment.subject ?? ''}`}>
                {assignment.group}
                {assignment.subject ? ` · ${assignment.subject}` : ` · ${t('invite.homeroom')}`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <form onSubmit={(event) => void submit(event)} className="mt-4 space-y-4" noValidate>
        <FormError message={error} />
        <p className="text-sm text-gray-600">{t('invite.email', { email: data.email })}</p>
        <Input
          label={t('login.newPassword.field')}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        <Input
          label={t('login.newPassword.confirm')}
          type="password"
          autoComplete="new-password"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          required
        />
        <Button type="submit" disabled={busy} className="w-full">
          {t('invite.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
};
